import { useState, useRef, useEffect, useCallback } from "react";
import {
  XMarkIcon,
  PaperClipIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentIcon,
  TrashIcon,
  InformationCircleIcon,
  CloudArrowUpIcon,
  ArrowUturnLeftIcon
} from "@heroicons/react/24/outline";
import {
  createTaskReport,
  deleteDocument,
} from "../../api/taskReportApi";
import UploadDocumentModal from "../../components/UploadDocumentModal"; // Your existing component


// Simple ObjectId validation
const isValidObjectId = (id) => {
  return typeof id === 'string' && id.length === 24 && /^[0-9a-fA-F]{24}$/.test(id);
};


/* Toast Notification Component */
const Toast = ({ message, onUndo, onClose, timeout = 5000 }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, timeout);
    return () => clearTimeout(timer);
  }, [onClose, timeout]);


  return (
    <div className="fixed bottom-6 right-6 z-[70] flex items-center justify-between p-4 space-x-4 bg-gray-900 text-white rounded-lg shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center space-x-3">
        <ExclamationCircleIcon className="w-5 h-5 text-gray-300" />
        <p className="text-sm">{message}</p>
      </div>
      {onUndo && (
        <button
          onClick={onUndo}
          className="flex items-center space-x-2 text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
          <span>Undo</span>
        </button>
      )}
    </div>
  );
};


/* File Display Component */
const FileItem = ({ file, onRemove }) => {
  const [isRemoving, setIsRemoving] = useState(false);


  const handleRemove = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsRemoving(true);
    await onRemove(file);
  };


  const getFileTypeColor = (fileName) => {
    if (!fileName || typeof fileName !== 'string') {
      return 'text-gray-600 bg-gray-50';
    }
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'text-red-600 bg-red-50';
      case 'docx': case 'doc': return 'text-blue-600 bg-blue-50';
      case 'xlsx': case 'xls': return 'text-green-600 bg-green-50';
      case 'txt': return 'text-gray-600 bg-gray-50';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };


  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-300">
      <div className="flex items-center p-4 space-x-4">
        <div className={`flex-shrink-0 p-3 rounded-lg transition-colors ${getFileTypeColor(file.originalName)}`}>
          <DocumentIcon className="w-6 h-6" />
        </div>


        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate pr-2">
            {file.originalName}
          </p>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-xs font-medium text-green-600">Ready</span>
          </div>
        </div>


        <button
          type="button"
          onClick={handleRemove}
          disabled={isRemoving}
          className={`p-2 rounded-lg transition-all duration-200 ${
            isRemoving
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100'
          }`}
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};


/* Main Modal */
const TaskReportModal = ({ isOpen, onClose, task, staffId = null, onUploadSuccess }) => {
  const [reportTitle, setReportTitle] = useState("");
  const [description, setDescription] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [toast, setToast] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);


  const modalRef = useRef(null);
  const reportTitleRef = useRef(null);
  const descriptionRef = useRef(null);
  const messageRef = useRef(null);


  // Debug: Log task to verify its value
  useEffect(() => {
    if (isOpen) {
      console.log("TaskReportModal: task =", task);
    }
  }, [isOpen, task]);


  const handleClose = useCallback(() => {
    if (loading) {
      setToast({
        message: "Report creation cancelled.",
        onClose: () => {
          setToast(null);
          onClose();
        },
      });
      setLoading(false);
      return;
    }
    
    setToast(null);
    onClose();
  }, [loading, onClose]);


  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReportTitle("");
      setDescription("");
      setUploadedDocuments([]);
      setFormErrors({});
      setMessage(null);
      setCurrentStep(1);
      setToast(null);
      setIsUploadModalOpen(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setIsUploadModalOpen(false);
      if (toast && typeof toast.onClose === 'function') {
        toast.onClose();
      }
      setToast(null);
    }
  }, [isOpen, toast]);


  // Enhanced keyboard navigation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !isUploadModalOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose, isOpen, isUploadModalOpen]);


  // FIXED: Click outside to close with proper nested modal handling
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if upload modal is open
      if (isUploadModalOpen) {
        return;
      }
      
      // Check if the click is outside the main modal
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        // Additional safety checks for modal-related elements
        const isModalElement = e.target.closest('[data-modal="true"]') ||
                              e.target.closest('[role="dialog"]') ||
                              e.target.matches('input[type="file"]') ||
                              e.target.closest('.fixed.inset-0.z-\\[60\\]') ||
                              e.target.closest('.fixed.inset-0.z-\\[70\\]');
        
        if (!isModalElement) {
          handleClose();
        }
      }
    };

    if (isOpen) {
      // Use capture phase to intercept events early
      document.addEventListener("mousedown", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [handleClose, isUploadModalOpen, isOpen]);


  // Auto-scroll to messages
  useEffect(() => {
    if (message && messageRef.current) {
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }, [message]);


  // Handle successful upload from UploadDocumentModal - same pattern as TimesheetFormModal
  const handleUploadSuccess = useCallback((files) => {
    console.log("Upload success received:", files);
    setUploadedDocuments(files);
    setIsUploadModalOpen(false);
    
    if (formErrors.files) {
      setFormErrors(prev => ({ ...prev, files: null }));
    }
  }, [formErrors.files]);


  const handleRemoveFile = useCallback(async (file) => {
    if (loading) return;


    setUploadedDocuments(prev => prev.filter(f => f !== file));


    // If the file has a document ID, delete it from the server
    if (file._id) {
      try {
        await deleteDocument(file._id);
      } catch (e) {
        console.error("Failed to delete document:", e);
      }
    }


    if (formErrors.files) {
      setFormErrors(prev => ({ ...prev, files: null }));
    }
  }, [loading, formErrors.files]);


  const validateForm = useCallback(() => {
    const errors = {};


    if (!reportTitle.trim()) {
      errors.reportTitle = "Report title is required";
    } else if (reportTitle.trim().length < 3) {
      errors.reportTitle = "Report title must be at least 3 characters";
    } else if (reportTitle.trim().length > 100) {
      errors.reportTitle = "Report title cannot exceed 100 characters";
    }


    if (description && description.trim().length > 0 && description.trim().length < 10) {
      errors.description = "Description must be at least 10 characters if provided";
    }


    if (!uploadedDocuments || uploadedDocuments.length === 0) {
      errors.files = "At least one file is required";
    }


    setFormErrors(errors);


    if (Object.keys(errors).length > 0) {
      setTimeout(() => {
        if (errors.reportTitle && reportTitleRef.current) {
          reportTitleRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          reportTitleRef.current.focus();
        } else if (errors.description && descriptionRef.current) {
          descriptionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          descriptionRef.current.focus();
        }
      }, 150);
    }


    return Object.keys(errors).length === 0;
  }, [reportTitle, description, uploadedDocuments]);


  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!validateForm()) return;


    if (!isValidObjectId(task?.id)) {
      setMessage({
        type: "error",
        text: "Invalid task ID. Please select a valid task."
      });
      setLoading(false);
      setCurrentStep(1);
      return;
    }


    if (!staffId || !isValidObjectId(staffId)) {
      setMessage({
        type: "error",
        text: "Staff ID is missing or invalid. Please ensure you are logged in."
      });
      setLoading(false);
      setCurrentStep(1);
      return;
    }


    setLoading(true);
    setMessage(null);
    setCurrentStep(2);


    try {
      setMessage({ type: "info", text: "Creating task report..." });


      // Get document IDs from uploaded documents - same pattern as TimesheetFormModal
      const documentIds = uploadedDocuments
        .filter(doc => doc._id)
        .map(doc => doc._id);


      if (!documentIds.length) {
        setMessage({ type: "error", text: "At least one document is required." });
        setCurrentStep(1);
        return;
      }


      const payload = {
        taskId: task.id,
        reportTitle: reportTitle.trim(),
        description: description?.trim().length >= 10 ? description.trim() : undefined,
        staffId,
        documentIds
      };


      const savedReport = await createTaskReport(payload);


      setMessage({
        type: "success",
        text: "Task report created successfully! You'll be redirected shortly..."
      });


      setTimeout(() => {
        onUploadSuccess && onUploadSuccess(savedReport);
        onClose();
      }, 3000);
    } catch (err) {
      console.error("Create report error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setMessage({
        type: "error",
        text: err.response?.data?.message ||
              err.message.includes('Task not found') ? 'Selected task is invalid.' :
              err.message.includes('Staff not found') ? 'Invalid staff ID.' :
              err.message.includes('documents do not exist') ? 'One or more uploaded files are invalid.' :
              `Failed to create task report: ${err.message}`
      });
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Upload button clicked, opening modal...");
    setIsUploadModalOpen(true);
  }, []);


  if (!isOpen) return null;


  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div
          ref={modalRef}
          data-modal="true"
          role="dialog"
          aria-labelledby="task-report-modal-title"
          aria-describedby="task-report-modal-description"
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Enhanced Header with Progress */}
          <div className="flex-shrink-0 bg-gradient-to-r from-orange-100 to-amber-100 border-b border-orange-100">
            <div className="flex items-center justify-between px-8 py-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-200 rounded-xl shadow-sm">
                  <PaperClipIcon className="w-7 h-7 text-orange-600" />
                </div>
                <div>
                  <h2 id="task-report-modal-title" className="text-2xl font-bold text-gray-900">Task Report Submission</h2>
                  <p id="task-report-modal-description" className="text-lg text-gray-600 mt-1">
                    For: {task?.title || "No task selected or title missing"}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-xl transition-all duration-200 text-gray-400 hover:bg-white hover:text-gray-600 hover:shadow-sm"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>


            {/* Progress Steps */}
            {currentStep > 1 && (
              <div className="px-8 pb-4">
                <div className="flex items-center space-x-4">
                  {[1, 2].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-3 h-3 rounded-full transition-colors ${
                        step < currentStep
                          ? 'bg-green-500'
                          : step === currentStep
                            ? 'bg-orange-500'
                            : 'bg-gray-300'
                      }`} />
                      {step < 2 && <div className="w-12 h-0.5 bg-gray-300 mx-2" />}
                    </div>
                  ))}
                  <span className="text-xs text-gray-600 ml-3">
                    {currentStep === 2 ? 'Creating report...' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>


          {/* Enhanced Body */}
          <div className="flex-grow px-8 py-6 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Status Message */}
              {message && (
                <div
                  ref={messageRef}
                  className={`animate-in slide-in-from-top-3 duration-500 flex items-start space-x-4 p-5 rounded-2xl border ${
                    message.type === "success"
                      ? "bg-green-50 border-green-200 text-green-800"
                      : message.type === "error"
                        ? "bg-red-50 border-red-200 text-red-800"
                        : "bg-blue-50 border-blue-200 text-blue-800"
                  }`}
                >
                  {message.type === "success" ? (
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  ) : message.type === "error" ? (
                    <ExclamationCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  ) : (
                    <InformationCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="font-medium">{message.text}</div>
                </div>
              )}


              {/* Report Title */}
              <div ref={reportTitleRef}>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Report Title</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <input
                  type="text"
                  value={reportTitle}
                  onChange={(e) => {
                    setReportTitle(e.target.value);
                    if (formErrors.reportTitle) {
                      setFormErrors(prev => ({ ...prev, reportTitle: null }));
                    }
                  }}
                  placeholder="e.g., Marketing Campaign Analysis"
                  disabled={loading}
                  className={`w-full rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    formErrors.reportTitle ? "border-red-300 bg-red-50" : "border-gray-300"
                  }`}
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    3-100 characters • Be specific and descriptive
                  </p>
                  <span className={`text-xs ${
                    reportTitle.length > 90 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    {reportTitle.length}/100
                  </span>
                </div>
                {formErrors.reportTitle && (
                  <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    <span>{formErrors.reportTitle}</span>
                  </p>
                )}
              </div>


              {/* Description */}
              <div ref={descriptionRef}>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Description</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    Optional
                  </span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (formErrors.description) {
                      setFormErrors(prev => ({ ...prev, description: null }));
                    }
                  }}
                  placeholder="Provide additional context about this report..."
                  disabled={loading}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                  rows={4}
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    {description.trim().length > 0 ? 'Minimum 10 characters if provided' : 'Add context about your report'}
                  </p>
                  <span className="text-xs text-gray-400">
                    {description.length}/500
                  </span>
                </div>
                {formErrors.description && (
                  <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    <span>{formErrors.description}</span>
                  </p>
                )}
              </div>


              {/* Supporting Documents Section - Same pattern as TimesheetFormModal */}
              <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                <h3 className="text-lg font-semibold text-gray-700">Supporting Documents</h3>
                <button
                  type="button"
                  onClick={handleUploadButtonClick}
                  disabled={loading}
                  className="w-full border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CloudArrowUpIcon className="mx-auto h-8 w-8 mb-2" />
                  Click to Upload Documents
                </button>


                {/* Display uploaded files */}
                {uploadedDocuments.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-800">
                      Uploaded Files ({uploadedDocuments.length})
                    </h4>
                    <div className="space-y-2">
                      {uploadedDocuments.map((file, index) => (
                        <FileItem
                          key={`${file.originalName}-${index}`}
                          file={file}
                          onRemove={handleRemoveFile}
                        />
                      ))}
                    </div>
                  </div>
                )}


                {formErrors.files && (
                  <p className="text-sm text-red-600 flex items-center space-x-1">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    <span>{formErrors.files}</span>
                  </p>
                )}
              </div>
            </form>
          </div>


          {/* Enhanced Footer */}
          <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {uploadedDocuments.length > 0 && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>{uploadedDocuments.length} file{uploadedDocuments.length === 1 ? '' : 's'} ready</span>
                  </div>
                )}
              </div>


              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>


                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={loading || !reportTitle.trim() || uploadedDocuments.length === 0}
                  className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                    loading
                      ? 'bg-orange-400 cursor-not-allowed'
                      : !reportTitle.trim() || uploadedDocuments.length === 0
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:scale-105 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating Report...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <CloudArrowUpIcon className="w-5 h-5" />
                      <span>Create Report</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Your existing UploadDocumentModal - Same pattern as TimesheetFormModal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          console.log("Closing upload modal...");
          setIsUploadModalOpen(false);
        }}
        staffId={staffId}
        onUploadSuccessWithFiles={handleUploadSuccess}
        category="Task Report"
      />


      {toast && (
        <Toast
          message={toast.message}
          onUndo={toast.onUndo}
          onClose={toast.onClose}
        />
      )}
    </>
  );
};


export default TaskReportModal;
